import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import LanguageToggle from './LanguageToggle';
import { useT } from '../lib/i18n';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useT();

  const navItems = [
    { path: '/', label: t('nav.home') },
    { path: '/gallery', label: t('nav.gallery') },
    { path: '/artists', label: t('nav.artists') },
    { path: '/annex', label: t('nav.annex') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-cream border-b-2 border-gold shadow-md">
      <div className="max-w-7xl mx-auto px-6 py-5">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="flex flex-col items-center">
              <div className="flex items-center gap-3">
                <div className="w-8 h-px bg-gold"></div>
                <div className="w-2 h-2 rotate-45 bg-dark-crimson border border-gold"></div>
                <span
                  className="text-base font-bold text-dark-green tracking-[0.45em] group-hover:text-rich-green transition-colors"
                  style={{ fontFamily: 'Playfair Display, serif' }}
                >
                  THE
                </span>
                <div className="w-2 h-2 rotate-45 bg-dark-crimson border border-gold"></div>
                <div className="w-8 h-px bg-gold"></div>
              </div>
              <span
                className="text-3xl font-bold text-dark-green tracking-wider group-hover:text-rich-green transition-colors -mt-1"
                style={{ fontFamily: 'Playfair Display, serif', letterSpacing: '0.15em' }}
              >
                CHIMERA
              </span>
              <div className="mt-1 flex items-center gap-2">
                <div className="h-px w-12 bg-dark-crimson"></div>
                <span className="text-xs text-gold tracking-[0.3em] font-semibold">GALLERY</span>
                <div className="h-px w-12 bg-dark-crimson"></div>
              </div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`text-sm font-semibold tracking-wider transition-colors duration-300 relative group ${
                  isActive(item.path) ? 'text-dark-crimson' : 'text-dark-green hover:text-dark-crimson'
                }`}
                style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: '1rem', letterSpacing: '0.1em' }}
              >
                {item.label}
                <span className={`absolute -bottom-2 left-0 h-0.5 bg-gold transition-all duration-300 ${
                  isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'
                }`}></span>
              </Link>
            ))}
            <LanguageToggle className="ml-2" />
          </nav>

          {/* Mobile Menu Button */}
          <div className="flex items-center gap-3 md:hidden">
            <LanguageToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="bg-dark-crimson text-cream hover:bg-crimson active:bg-crimson transition-colors border border-gold shadow-md p-2 rounded-none"
              aria-label="Menu"
              data-testid="mobile-menu-toggle"
            >
              {isMenuOpen ? <X size={26} strokeWidth={2.5} /> : <Menu size={26} strokeWidth={2.5} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav className="md:hidden mt-6 pb-4 space-y-4 border-t border-gold pt-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setIsMenuOpen(false)}
                className={`block text-lg font-semibold transition-colors duration-300 ${
                  isActive(item.path) ? 'text-dark-crimson' : 'text-dark-green hover:text-dark-crimson'
                }`}
                style={{ fontFamily: 'Cormorant Garamond, serif' }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
